import { IonCard } from '@ionic/react';
import { useNavigate } from 'react-router-dom';
import type { GetMatchesQuery } from '../../../../types/graphql-generated';

type Match = NonNullable<GetMatchesQuery['getMatches']>['matches'][number];

interface MatchListProps {
  matches: Match[];
  // Where match details live, so other sections (e.g. the admin area) can open them in their own stack.
  matchPathPrefix?: string;
}

export const MatchList: React.FC<MatchListProps> = ({
  matches,
  matchPathPrefix = '/app/main/competition/partidos',
}) => {
  const navigate = useNavigate();

  const formatMatchDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('es-ES', {
      timeZone: 'Europe/Madrid',
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleMatchClick = (matchId: string) => {
    navigate(`${matchPathPrefix}/${matchId}`);
  };

  if (matches.length === 0) {
    return (
      <div style={{ padding: '16px', textAlign: 'center', color: '#666' }}>
        No hay partidos disponibles
      </div>
    );
  }

  return (
    <div style={{ padding: '12px 16px 8px' }}>
      {matches.map((match) => (
        <IonCard
          key={match.id}
          onClick={() => handleMatchClick(match.id)}
          style={{
            cursor: 'pointer',
            margin: '0 0 12px',
            borderRadius: '16px',
            border: '1px solid #e8ebf1',
            boxShadow: '0 1px 2px rgba(20, 30, 60, 0.05)',
          }}
        >
          <div style={{ padding: '16px' }}>
            {/* Match row */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '8px',
              }}
            >
              {/* Home team */}
              <div
                data-testid="home-team"
                style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}
              >
                {match.homeTeam.shield && (
                  <img
                    src={match.homeTeam.shield}
                    alt={match.homeTeam.nameShow}
                    style={{ width: '24px', height: '24px', flexShrink: 0, objectFit: 'contain' }}
                  />
                )}
                {!match.homeTeam.shield && <span aria-hidden="true" style={{ width: '24px', flexShrink: 0 }} />}
                <span
                  style={{
                    minWidth: 0,
                    flex: 1,
                    fontWeight: 500,
                    lineHeight: 1.35,
                    overflowWrap: 'break-word',
                    textAlign: 'left',
                  }}
                >
                  {match.homeTeam.nameShow}
                </span>
              </div>

              {/* Score */}
              <div style={{ fontWeight: 'bold', fontSize: '18px', minWidth: '60px', textAlign: 'center' }}>
                {match.status === 'finished' ? (
                  `${match.goalsHome} - ${match.goalsAway}`
                ) : (
                  <span style={{ color: '#666' }}>vs</span>
                )}
              </div>

              {/* Away team */}
              <div
                data-testid="away-team"
                style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}
              >
                <span
                  style={{
                    minWidth: 0,
                    flex: 1,
                    fontWeight: 500,
                    lineHeight: 1.35,
                    overflowWrap: 'break-word',
                    textAlign: 'right',
                  }}
                >
                  {match.awayTeam.nameShow}
                </span>
                {match.awayTeam.shield && (
                  <img
                    src={match.awayTeam.shield}
                    alt={match.awayTeam.nameShow}
                    style={{ width: '24px', height: '24px', flexShrink: 0, objectFit: 'contain' }}
                  />
                )}
                {!match.awayTeam.shield && <span aria-hidden="true" style={{ width: '24px', flexShrink: 0 }} />}
              </div>
            </div>

            {/* Match date */}
            <div style={{ textAlign: 'center', fontSize: '12px', color: '#666' }}>
              {formatMatchDate(match.matchDate)}
            </div>
          </div>
        </IonCard>
      ))}
    </div>
  );
};
